import React, { useState } from 'react';
import { useNotification } from '../../hooks/NotificationContext';
import { archiveTestCase, bulkCreateTestCase, deleteTestCase } from '../../modules/entity-details';
import { TestCaseToolbar } from '../../ui/primitives/ActionToolbar';
import FileImportComponent from '../../components/elements/file-import/FileImportComponent';
import FileExportComponent from '../../components/elements/file-export';
import ConfirmationModal from '../../ui/primitives/ConfirmationModal';

const TestCaseTableActions = ({
  handleOpenTestCaseModal,
  selectedItems: selectedItemsProp,
  seletectItems,
  showArchived,
  isSelectedItems,
  selectedRows,
  setShowArchived,
  refreshData,
  projectId,
  handleBulkEditModal,
  setSelected,
  fetchTestCaseData,
  pendingAction,
  setPendingAction,
}) => {
  const { addNotification } = useNotification();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const selectedItems = (selectedItemsProp ?? seletectItems) ?? [];
  const safeSelectedRows = selectedRows ?? [];
  const actionCount = pendingAction?.row ? 1 : selectedItems.length;
  const actionLabel = actionCount === 1 ? 'this test case' : `${actionCount} test cases`;

  const handleArchive = async () => {
    const ids = pendingAction?.row
      ? [pendingAction.row.id]
      : selectedItems;

    try {
      await archiveTestCase({ testcases: ids, type: 'archived' });
      addNotification('success', 'Test case(s) archived successfully!');
      refreshData();
      setShowArchiveModal(false);
      setPendingAction(null);
    } catch (error) {
      addNotification('danger', error?.message ?? 'Failed to archive test case. Please try again later.');
    }
  };

  const handleUnarchive = async () => {
    try {
      await archiveTestCase({ testcases: selectedItems, type: 'unarchived' });
      addNotification('success', 'Test case(s) unarchived successfully!');
      refreshData();
    } catch (error) {
      addNotification('danger', error?.message ?? 'Failed to unarchive test case. Please try again later.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTestCase({ testcases: selectedItems });
      addNotification('success', 'Test case(s) deleted successfully!');
      refreshData();
      setShowDeleteModal(false);
    } catch (error) {
      addNotification('danger', error?.message ?? 'Failed to delete test case. Please try again later.');
    }
  };

  const handleClone = async () => {
    const ids = pendingAction?.row
      ? [pendingAction.row.id]
      : safeSelectedRows.map((tc) => tc.id);

    try {
      await bulkCreateTestCase({ testcaseIds: ids }, projectId);
      addNotification('success', 'Test case(s) cloned successfully!');
      refreshData();
      setShowCloneModal(false);
      setPendingAction(null);
    } catch (error) {
      addNotification('danger', error?.message ?? 'Failed to clone test case. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowCloneModal(false);
    setShowArchiveModal(false);
    setPendingAction(null);
  }


  return (
    <>
      <TestCaseToolbar
        onAdd={handleOpenTestCaseModal}
        onBulkEdit={() => handleBulkEditModal(true)}
        onClone={() => setShowCloneModal(true)}
        onArchive={() => setShowArchiveModal(true)}
        onUnarchive={handleUnarchive}
        onImport={() => setImportModalOpen(true)}
        onExport={() => setExportModalOpen(true)}
        onToggleArchived={setShowArchived}
        showArchived={showArchived}
        isSelectedItems={isSelectedItems}
      />

      <ConfirmationModal
        show={showCloneModal || pendingAction?.type === 'clone'}
        handleClose={handleCloseModal}
        handleConfirm={handleClone}
        title="Confirm Clone"
        body={`Are you sure you want to clone ${actionLabel}?`}
        confirmLabel="Clone"
        cancelLabel="Cancel"
      />

      <ConfirmationModal
        show={showArchiveModal || pendingAction?.type === 'archive'}
        handleClose={handleCloseModal}
        handleConfirm={handleArchive}
        title="Confirm Archive"
        body={`Are you sure you want to archive ${actionLabel}?`}
        confirmLabel="Archive"
        cancelLabel="Cancel"
      />

      <ConfirmationModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleConfirm={handleDelete}
        title="Delete Confirmation"
        body="Are you sure you want to delete these test case(s)?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      <FileImportComponent
        show={importModalOpen}
        handleClose={() => setImportModalOpen(false)}
        projectId={projectId}
        fetchTestCases={refreshData}
        setSelected={setSelected}
        fetchTestCaseData={fetchTestCaseData}
      />

      {exportModalOpen && (
        <FileExportComponent
          show={exportModalOpen}
          handleClose={() => setExportModalOpen(false)}
          fetchTestCases={refreshData}
          projectId={projectId}
          selectedTestcases={selectedRows}
        />
      )}
    </>
  );
};

export default TestCaseTableActions;